import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Link, useLocation } from "wouter";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { TrendingUp, Eye, Clock, Medal, Newspaper, ShoppingBag, Youtube, Flame, Building2, Package, Megaphone, Star, CheckCircle, Award, MapPinned, Phone, Mail, Lock, CheckCircle2, Shield, Info, UtensilsCrossed } from "lucide-react";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { toArray } from "@/lib/array";
import type { Article, Business, Video, MarketListing, MarketplaceItem, Restaurant } from "@shared/schema";
import { verificationTypes } from "@shared/schema";
import { PageContainer } from "@/components/layout/PageContainer";

export default function Rankings() {
  const { membershipLevel } = useAuth();
  const [, setLocation] = useLocation();
  const [selectedType, setSelectedType] = useState("articles");
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");

  // Fetch all data with safe array conversion
  const { data: rawArticles, isLoading: articlesLoading } = useQuery({
    queryKey: ['/api/articles'],
  });
  const articles = toArray<Article>(rawArticles);

  const { data: rawBusinesses, isLoading: businessesLoading } = useQuery({
    queryKey: ['/api/business-directory'],
  });
  const businesses = toArray<Business>(rawBusinesses);

  const { data: rawVideos, isLoading: videosLoading } = useQuery({
    queryKey: ['/api/videos'],
  });
  const videos = toArray<Video>(rawVideos);

  const { data: rawMarketListings, isLoading: marketListingsLoading } = useQuery({
    queryKey: ['/api/market'],
  });
  const marketListings = toArray<MarketListing>(rawMarketListings);

  const { data: rawMarketplaceItems, isLoading: marketplaceLoading } = useQuery({
    queryKey: ['/api/marketplace/items'],
  });
  const marketplaceItems = toArray<MarketplaceItem>(rawMarketplaceItems);

  const { data: rawRestaurants, isLoading: restaurantsLoading } = useQuery({
    queryKey: ['/api/restaurants'],
  });
  const restaurants = toArray<Restaurant>(rawRestaurants);

  // Sort articles by views (arrays are now guaranteed by toArray)
  const topArticles = articles
    .filter(a => (a.status === 'published' || a.status === 'approved') && a.views && a.views > 0)
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 100);

  // Sort businesses by views
  const topBusinesses = businesses
    .filter(b => b.status === 'active' && b.views && b.views > 0)
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 100);

  // Sort videos by views
  const topVideos = videos
    .filter(v => v.status === 'active' && v.views && v.views > 0)
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 100);

  // Sort market listings by views (동네마켓)
  const topMarketListings = marketListings
    .filter(m => m.status === 'available' && m.views && m.views > 0)
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 100);

  // Sort marketplace items by views
  const topMarketplaceItems = marketplaceItems
    .filter(m => m.status === 'active' && m.views && m.views > 0)
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 100);

  // Sort restaurants by views
  const topRestaurants = restaurants
    .filter(r => r.status === 'active' && r.views && r.views > 0)
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 100);

  const getMedalColor = (rank: number) => {
    if (rank === 1) return "text-yellow-500";
    if (rank === 2) return "text-gray-400";
    if (rank === 3) return "text-amber-600";
    return "text-muted-foreground";
  };

  const getRankBadge = (rank: number) => {
    if (rank <= 3) {
      return <Medal className={`h-6 w-6 ${getMedalColor(rank)}`} />;
    }
    return <span className="text-2xl font-bold text-muted-foreground">#{rank}</span>;
  };

  // Check if user can access premium features
  const canAccessPremiumFeatures = () => {
    return membershipLevel === "premium" || membershipLevel === "platinum";
  };

  // Handle premium feature click
  const handlePremiumFeatureClick = (e: React.MouseEvent, featureName: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canAccessPremiumFeatures()) {
      setUpgradeMessage(`"${featureName}" 기능은 프리미엄 회원 전용입니다. 업그레이드하고 더 많은 정보를 확인하세요!`);
      setUpgradeDialogOpen(true);
    }
  };

  // Get verification badges for a business
  const getVerificationBadges = (business: Business) => {
    const badges = [];

    if (business.isPartnerVerified) {
      const partnerVerification = verificationTypes.find(v => v.id === "partner");
      badges.push({ ...partnerVerification, priority: 3 });
    }
    if (business.isPremiumVerified) {
      const premiumVerification = verificationTypes.find(v => v.id === "premium");
      badges.push({ ...premiumVerification, priority: 2 });
    }
    if (business.isVerified) {
      const basicVerification = verificationTypes.find(v => v.id === "basic");
      badges.push({ ...basicVerification, priority: 1 });
    }

    return badges;
  };

  // Render verification icon based on type
  const renderVerificationIcon = (iconName: string, color?: string, isVerified: boolean = true) => {
    const className = `h-4 w-4 ${
      !isVerified ? 'text-gray-400' :
      color === 'green' ? 'text-green-600' :
      color === 'blue' ? 'text-blue-600' :
      'text-amber-600'
    }`;

    switch(iconName) {
      case 'CheckCircle2':
        return <CheckCircle2 className={className} />;
      case 'Star':
        return <Star className={className} />;
      case 'Shield':
        return <Shield className={className} />;
      default:
        return null;
    }
  };

  // Render all verification icons (gray if not verified, colored if verified)
  const renderAllVerificationIcons = (business: Business) => {
    return verificationTypes.map((verification) => {
      const isVerified = !!(
        (verification.id === 'basic' && business.isVerified) ||
        (verification.id === 'premium' && business.isPremiumVerified) ||
        (verification.id === 'partner' && business.isPartnerVerified)
      );

      return (
        <Tooltip key={verification.id}>
          <TooltipTrigger asChild>
            <span
              className="cursor-help inline-block"
              data-testid={`badge-verification-${verification.id}-${business.id}`}
            >
              {renderVerificationIcon(verification.icon, verification.color, isVerified)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="font-semibold mb-1">{verification.nameKo}</p>
            <p className="text-xs">{verification.description}</p>
            {!isVerified && (
              <p className="text-xs text-muted-foreground mt-1">
                * 프리미엄 업체만 인증 획득 가능
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      );
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background border-b">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold">인기 랭킹</h1>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl">
            가장 많이 본 콘텐츠를 확인하세요 - 기사, 영상, 업체, 상품 랭킹
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={selectedType} onValueChange={setSelectedType}>
          <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6 mb-8">
            <TabsTrigger value="articles" data-testid="tab-articles">
              <Newspaper className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">인기 </span>기사
            </TabsTrigger>
            <TabsTrigger value="businesses" data-testid="tab-businesses">
              <Building2 className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">인기 </span>업체
            </TabsTrigger>
            <TabsTrigger value="videos" data-testid="tab-videos">
              <Youtube className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">인기 </span>영상
            </TabsTrigger>
            <TabsTrigger value="restaurants" data-testid="tab-restaurants">
              <UtensilsCrossed className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">인기 </span>맛집
            </TabsTrigger>
            <TabsTrigger value="marketplace" data-testid="tab-marketplace">
              <ShoppingBag className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">핫샵 </span>상품
            </TabsTrigger>
            <TabsTrigger value="market" data-testid="tab-market">
              <Package className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">중고</span>마켓
            </TabsTrigger>
          </TabsList>

          {/* Articles Rankings */}
          <TabsContent value="articles">
            {articlesLoading ? (
              <div className="space-y-4">
                {[...Array(10)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <Skeleton className="h-12 w-12 rounded-full" />
                        <div className="flex-1">
                          <Skeleton className="h-6 w-3/4 mb-2" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : topArticles.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <Flame className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold mb-2">랭킹 데이터가 없습니다</h3>
                  <p className="text-muted-foreground">아직 조회수가 기록된 기사가 없습니다</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {topArticles.map((article, index) => (
                  <Card
                    key={article.id}
                    className="hover-elevate active-elevate-2 cursor-pointer transition-all"
                    onClick={() => setLocation(`/article/${article.id}`)}
                    data-testid={`rank-article-${index + 1}`}
                  >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-12 flex items-center justify-center">
                            {getRankBadge(index + 1)}
                          </div>

                          {article.imageUrl && (
                            <div className="w-32 h-20 rounded-md overflow-hidden flex-shrink-0">
                              <img
                                src={article.imageUrl}
                                alt={article.titleKo || article.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold mb-2 line-clamp-2">
                              {article.titleKo || article.title}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                              <div className="flex items-center gap-1">
                                <Eye className="h-4 w-4" />
                                <span>{(article.views || 0).toLocaleString()} 조회</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                <span>{format(new Date(article.publishedAt || article.createdAt), "MM/dd", { locale: ko })}</span>
                              </div>
                              {article.isBreaking && (
                                <Badge variant="destructive" className="text-xs">속보</Badge>
                              )}
                            </div>
                          </div>

                          {index < 10 && (
                            <Badge variant="secondary" className="flex-shrink-0">
                              TOP {index + 1}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Businesses Rankings */}
          <TabsContent value="businesses">
            {/* Icon Guide */}
            <div className="bg-muted/30 border rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Info className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-sm">아이콘 안내</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>전화번호</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>이메일</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span>사업체 확인</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Star className="h-4 w-4 text-blue-600" />
                  <span>만족도 검증</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Shield className="h-4 w-4 text-amber-600" />
                  <span>파트너십</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Award className="h-4 w-4 text-yellow-600" />
                  <span>프리미엄</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <ShoppingBag className="h-4 w-4 text-orange-600" />
                  <span>핫샵&딜</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPinned className="h-4 w-4 text-blue-600" />
                  <span>지도 위치</span>
                </div>
              </div>
            </div>

            {businessesLoading ? (
              <div className="space-y-4">
                {[...Array(10)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <Skeleton className="h-12 w-12 rounded-full" />
                        <div className="flex-1">
                          <Skeleton className="h-6 w-3/4 mb-2" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : topBusinesses.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <Building2 className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold mb-2">랭킹 데이터가 없습니다</h3>
                  <p className="text-muted-foreground">아직 조회수가 기록된 업체가 없습니다</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {topBusinesses.map((business, index) => (
                  <Card
                    key={business.id}
                    className="hover-elevate active-elevate-2 cursor-pointer transition-all"
                    onClick={() => setLocation(`/business-directory/${business.id}`)}
                    data-testid={`rank-business-${index + 1}`}
                  >
                    <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-12 flex items-center justify-center">
                            {getRankBadge(index + 1)}
                          </div>

                          {business.logoUrl && (
                            <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                              <img
                                src={business.logoUrl}
                                alt={business.businessName}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold mb-2 line-clamp-1">
                              {business.businessNameKo || business.businessName}
                            </h3>

                            <p className="text-sm text-muted-foreground mb-2 line-clamp-1">
                              {business.shortDescription}
                            </p>

                            {/* Contact Info */}
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              {business.contactPhone && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <a
                                      href={`tel:${business.contactPhone}`}
                                      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
                                      onClick={(e) => e.stopPropagation()}
                                      data-testid={`link-phone-${business.id}`}
                                    >
                                      <Phone className="h-4 w-4" />
                                      <span>{business.contactPhone}</span>
                                    </a>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p className="text-xs">전화번호로 연락하기</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    className="flex items-center gap-1 text-sm"
                                    data-testid={`icon-email-${business.id}`}
                                  >
                                    <Mail className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <p className="text-xs">
                                    프리미엄 업체만 이메일 보내기가 가능합니다
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </div>

                            <div className="flex items-center gap-4 text-sm flex-wrap">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Eye className="h-4 w-4" />
                                <span>{(business.views || 0).toLocaleString()} 조회</span>
                              </div>
                              {business.averageRating && parseFloat(business.averageRating) > 0 && (
                                <div className="flex items-center gap-1">
                                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                  <span>{parseFloat(business.averageRating).toFixed(1)} ({business.reviewCount})</span>
                                </div>
                              )}

                              {/* Feature Icons */}
                              <div className="flex items-center gap-1">
                                {/* Verification Badges - All shown, gray if not verified */}
                                {renderAllVerificationIcons(business)}

                                {business.featuredUntil && new Date(business.featuredUntil) > new Date() && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-block" data-testid={`icon-featured-${business.id}`}>
                                        <Award className="h-4 w-4 text-yellow-600" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      <p className="text-xs">프리미엄 추천 업체</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                {business.hasProducts && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-block" data-testid={`icon-products-${business.id}`}>
                                        <ShoppingBag className="h-4 w-4 text-orange-600" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      <p className="text-xs">핫샵&딜 상품 보유</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                {((business.latitude && business.longitude) || business.address) && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-block" data-testid={`icon-map-${business.id}`}>
                                        <MapPinned className="h-4 w-4 text-blue-600" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      <p className="text-xs">지도에서 위치 보기</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                            </div>

                            {/* Cross-platform Integration */}
                            {business.hasProducts && (
                              <div className="bg-orange-500/10 rounded px-2 py-1 text-xs flex items-center gap-1 mt-2 w-fit">
                                <ShoppingBag className="h-3 w-3 text-orange-600" />
                                <span className="text-orange-700 dark:text-orange-400 font-medium">
                                  핫샵&딜 {business.productCount}개 상품
                                </span>
                              </div>
                            )}
                          </div>

                          {index < 10 && (
                            <Badge variant="secondary" className="flex-shrink-0">
                              TOP {index + 1}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Videos Rankings */}
          <TabsContent value="videos">
            {videosLoading ? (
              <div className="space-y-4">
                {[...Array(10)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <Skeleton className="h-12 w-12 rounded-full" />
                        <div className="flex-1">
                          <Skeleton className="h-6 w-3/4 mb-2" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : topVideos.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <Youtube className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold mb-2">랭킹 데이터가 없습니다</h3>
                  <p className="text-muted-foreground">아직 조회수가 기록된 영상이 없습니다</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {topVideos.map((video, index) => (
                  <Card
                    key={video.id}
                    className="hover-elevate active-elevate-2 cursor-pointer transition-all"
                    onClick={() => setLocation(`/youtube/${video.id}`)}
                    data-testid={`rank-video-${index + 1}`}
                  >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-12 flex items-center justify-center">
                            {getRankBadge(index + 1)}
                          </div>

                          {video.thumbnailUrl && (
                            <div className="w-40 h-24 rounded-md overflow-hidden flex-shrink-0">
                              <img
                                src={video.thumbnailUrl}
                                alt={video.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold mb-2 line-clamp-2">
                              {video.title}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                              <div className="flex items-center gap-1">
                                <Eye className="h-4 w-4" />
                                <span>{(video.views || 0).toLocaleString()} 조회</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                <span>{format(new Date(video.createdAt), "MM/dd", { locale: ko })}</span>
                              </div>
                              {video.isFeatured && (
                                <Badge variant="default" className="text-xs">추천</Badge>
                              )}
                            </div>
                          </div>

                          {index < 10 && (
                            <Badge variant="secondary" className="flex-shrink-0">
                              TOP {index + 1}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Restaurants Rankings */}
          <TabsContent value="restaurants">
            {restaurantsLoading ? (
              <div className="space-y-4">
                {[...Array(10)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <Skeleton className="h-12 w-12 rounded-full" />
                        <div className="flex-1">
                          <Skeleton className="h-6 w-3/4 mb-2" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : topRestaurants.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <UtensilsCrossed className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold mb-2">랭킹 데이터가 없습니다</h3>
                  <p className="text-muted-foreground">아직 조회수가 기록된 맛집이 없습니다</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {topRestaurants.map((restaurant, index) => (
                  <Card
                    key={restaurant.id}
                    className="hover-elevate active-elevate-2 cursor-pointer transition-all"
                    onClick={() => setLocation(`/marketplace?tab=restaurants&id=${restaurant.id}`)}
                    data-testid={`rank-restaurant-${index + 1}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 flex items-center justify-center">
                          {getRankBadge(index + 1)}
                        </div>

                        {(restaurant.logoUrl || restaurant.coverImageUrl) && (
                          <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                            <img
                              src={restaurant.logoUrl || restaurant.coverImageUrl || ''}
                              alt={restaurant.restaurantNameKo || restaurant.restaurantName}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold mb-2 line-clamp-1">
                            {restaurant.restaurantNameKo || restaurant.restaurantName}
                          </h3>

                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {restaurant.category === 'korean' && '한식'}
                              {restaurant.category === 'chinese' && '중식'}
                              {restaurant.category === 'japanese' && '일식'}
                              {restaurant.category === 'western' && '양식'}
                              {restaurant.category === 'vietnamese' && '베트남'}
                              {restaurant.category === 'thai' && '태국'}
                              {restaurant.category === 'cafe' && '카페'}
                              {restaurant.category === 'other' && '기타'}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                              {restaurant.priceRange === 'budget' && '$'}
                              {restaurant.priceRange === 'moderate' && '$$'}
                              {restaurant.priceRange === 'high' && '$$$'}
                            </Badge>
                          </div>

                          <p className="text-sm text-muted-foreground mb-2 line-clamp-1">
                            {restaurant.shortDescription}
                          </p>

                          <div className="flex items-center gap-4 text-sm flex-wrap">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Eye className="h-4 w-4" />
                              <span>{(restaurant.views || 0).toLocaleString()} 조회</span>
                            </div>
                            {restaurant.averageRating && parseFloat(restaurant.averageRating) > 0 && (
                              <div className="flex items-center gap-1">
                                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                <span>{parseFloat(restaurant.averageRating).toFixed(1)} ({restaurant.reviewCount})</span>
                              </div>
                            )}
                            {restaurant.featured && (
                              <Badge variant="default" className="text-xs">추천 맛집</Badge>
                            )}
                          </div>
                        </div>

                        {index < 10 && (
                          <Badge variant="secondary" className="flex-shrink-0">
                            TOP {index + 1}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Marketplace Items Rankings */}
          <TabsContent value="marketplace">
            {marketplaceLoading ? (
              <div className="space-y-4">
                {[...Array(10)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <Skeleton className="h-12 w-12 rounded-full" />
                        <div className="flex-1">
                          <Skeleton className="h-6 w-3/4 mb-2" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : topMarketplaceItems.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <ShoppingBag className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold mb-2">랭킹 데이터가 없습니다</h3>
                  <p className="text-muted-foreground">아직 조회수가 기록된 상품이 없습니다</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {topMarketplaceItems.map((item, index) => (
                  <Card
                    key={item.id}
                    className="hover-elevate active-elevate-2 cursor-pointer transition-all"
                    onClick={() => setLocation(`/marketplace/${item.id}`)}
                    data-testid={`rank-marketplace-${index + 1}`}
                  >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-12 flex items-center justify-center">
                            {getRankBadge(index + 1)}
                          </div>

                          {item.imageUrl && (
                            <div className="w-32 h-24 rounded-md overflow-hidden flex-shrink-0">
                              <img
                                src={item.imageUrl}
                                alt={item.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold mb-2 line-clamp-2">
                              {item.titleKo || item.title}
                            </h3>
                            <div className="flex items-center gap-4 text-sm flex-wrap">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Eye className="h-4 w-4" />
                                <span>{(item.views || 0).toLocaleString()} 조회</span>
                              </div>
                              <span className="text-lg font-bold text-primary">${item.salePrice}</span>
                              {item.discountRate && item.discountRate > 0 && (
                                <Badge variant="destructive" className="text-xs">
                                  {item.discountRate}% 할인
                                </Badge>
                              )}
                            </div>
                          </div>

                          {index < 10 && (
                            <Badge variant="secondary" className="flex-shrink-0">
                              TOP {index + 1}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Market Listings Rankings */}
          <TabsContent value="market">
            {marketListingsLoading ? (
              <div className="space-y-4">
                {[...Array(10)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <Skeleton className="h-12 w-12 rounded-full" />
                        <div className="flex-1">
                          <Skeleton className="h-6 w-3/4 mb-2" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : topMarketListings.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <Package className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold mb-2">랭킹 데이터가 없습니다</h3>
                  <p className="text-muted-foreground">아직 조회수가 기록된 매물이 없습니다</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {topMarketListings.map((listing, index) => (
                  <Card
                    key={listing.id}
                    className="hover-elevate active-elevate-2 cursor-pointer transition-all"
                    onClick={() => setLocation(`/market/${listing.id}`)}
                    data-testid={`rank-market-${index + 1}`}
                  >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-12 flex items-center justify-center">
                            {getRankBadge(index + 1)}
                          </div>

                          {listing.images?.[0] && (
                            <div className="w-32 h-24 rounded-md overflow-hidden flex-shrink-0">
                              <img
                                src={listing.images[0]}
                                alt={listing.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold mb-2 line-clamp-2">
                              {listing.title}
                            </h3>
                            <div className="flex items-center gap-4 text-sm flex-wrap">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Eye className="h-4 w-4" />
                                <span>{(listing.views || 0).toLocaleString()} 조회</span>
                              </div>
                              <span className="text-lg font-bold text-primary">
                                {listing.priceCents === 0 ? "무료나눔" : listing.priceCents === -1 ? "가격협의" : `$${(listing.priceCents / 100).toFixed(0)}`}
                              </span>
                              {listing.isPremium && (
                                <Badge variant="default" className="text-xs">프리미엄</Badge>
                              )}
                            </div>
                          </div>

                          {index < 10 && (
                            <Badge variant="secondary" className="flex-shrink-0">
                              TOP {index + 1}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              프리미엄 기능
            </DialogTitle>
            <DialogDescription>{upgradeMessage}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-primary/5 rounded-lg p-4 space-y-3">
              <h4 className="font-semibold text-sm">프리미엄 회원 혜택:</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <span>업체 안전인증 정보 확인</span>
                </li>
                <li className="flex items-center gap-2">
                  <ShoppingBag className="h-4 w-4 text-orange-600 flex-shrink-0" />
                  <span>핫샵&딜 연동 상품 보기</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPinned className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span>상세 지도 및 길찾기</span>
                </li>
                <li className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-purple-600 flex-shrink-0" />
                  <span>랭킹 차트 접근</span>
                </li>
                <li className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                  <span>추천 업체 상세 정보</span>
                </li>
                <li className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                  <span>광고 제거</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-lg p-4 border border-primary/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">프리미엄</span>
                <span className="text-2xl font-bold">$9.99<span className="text-sm text-muted-foreground">/월</span></span>
              </div>
              <p className="text-xs text-muted-foreground">첫 해 특별 프로모션 가격</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setUpgradeDialogOpen(false)}
              className="flex-1"
              data-testid="button-cancel-upgrade"
            >
              닫기
            </Button>
            <Button
              onClick={() => {
                setUpgradeDialogOpen(false);
                setLocation("/pricing");
              }}
              className="flex-1"
              data-testid="button-upgrade-now"
            >
              지금 업그레이드
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
